import React, { useMemo, forwardRef } from "react";
import PropTypes from "prop-types";
import { Autocomplete, TextField, Paper } from "@mui/material";

/** Outlined list surface + shadow so the dropdown reads clearly (dialogs and dense toolbars). */
export const ClientAutocompletePaper = forwardRef(function ClientAutocompletePaper(
  { sx, children, ...other },
  ref
) {
  return (
    <Paper
      ref={ref}
      variant="outlined"
      elevation={0}
      {...other}
      sx={[
        (theme) => ({
          mt: 0.5,
          borderRadius: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 10px 40px rgba(0, 0, 0, 0.55)"
              : "0 10px 40px rgba(15, 23, 42, 0.14)",
          overflow: "hidden",
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </Paper>
  );
});

function optionIdStr(option) {
  return option?.id == null ? "" : String(option.id);
}

function isSentinelOption(option, sentinelValue) {
  return optionIdStr(option) === String(sentinelValue);
}

function formatClientRowLabel(option) {
  const name = option?.name || "—";
  return option?.project ? `${name} (${option.project})` : name;
}

/**
 * Searchable client picker (MUI Autocomplete). First row is a sentinel (e.g. “All clients” or “None”).
 */
export default function ClientPickerAutocomplete({
  id,
  clients,
  value,
  onValueChange,
  sentinelValue,
  sentinelLabel,
  loading = false,
  disabled = false,
  label = "Client",
  placeholder = "Search or select a client…",
  insideModal = false,
  /** Match neighboring `Select` / `TextField` on dashboard toolbars (`medium`); use `small` in dense modals. */
  size = "medium",
  fullWidth = true,
  sx: sxRoot,
  /** Merged into the inner `TextField` (recommended for border/background parity with outlined controls). */
  inputSx,
}) {
  const sentinelOption = useMemo(
    () => ({ id: sentinelValue, name: "", project: null }),
    [sentinelValue]
  );

  const { options, selectedOption } = useMemo(() => {
    const raw = value == null ? "" : String(value);
    let opts = [sentinelOption, ...clients];
    let selected = sentinelOption;

    if (raw !== String(sentinelValue)) {
      const found = clients.find((c) => String(c.id) === raw);
      if (found) {
        selected = found;
      } else {
        const orphan = {
          id: raw,
          name: loading ? "Loading…" : "Unknown client",
          project: null,
        };
        opts = [...opts, orphan];
        selected = orphan;
      }
    }
    return { options: opts, selectedOption: selected };
  }, [clients, value, sentinelValue, sentinelOption, loading]);

  const getOptionLabel = (option) => {
    if (!option) return "";
    if (isSentinelOption(option, sentinelValue)) return sentinelLabel;
    return formatClientRowLabel(option);
  };

  return (
    <Autocomplete
      id={id}
      options={options}
      value={selectedOption}
      onChange={(_, newValue) => {
        if (!newValue) {
          onValueChange(String(sentinelValue));
          return;
        }
        onValueChange(String(newValue.id ?? ""));
      }}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(a, b) => optionIdStr(a) === optionIdStr(b)}
      loading={loading}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      sx={sxRoot}
      slots={{ paper: ClientAutocompletePaper }}
      slotProps={{
        popper: insideModal
          ? {
              sx: (theme) => ({ zIndex: theme.zIndex.modal + 1 }),
            }
          : {},
        listbox: {
          sx: {
            maxHeight: 280,
            py: 0.5,
            "& .MuiAutocomplete-option": {
              py: 1,
              alignItems: "flex-start",
            },
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          sx={[
            (theme) => ({
              "& .MuiOutlinedInput-root": {
                bgcolor: "background.paper",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.divider,
              },
              "& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot": {
                bgcolor: "background.paper",
              },
              // Theme `MuiOutlinedInput` sets `input` to `grey50`; override so the field isn’t two-toned.
              "& .MuiOutlinedInput-input, & .MuiInputBase-input": {
                bgcolor: "background.paper",
              },
            }),
            ...(Array.isArray(inputSx) ? inputSx : inputSx ? [inputSx] : []),
          ]}
        />
      )}
    />
  );
}

ClientPickerAutocomplete.propTypes = {
  id: PropTypes.string,
  clients: PropTypes.arrayOf(PropTypes.object).isRequired,
  value: PropTypes.string,
  onValueChange: PropTypes.func.isRequired,
  sentinelValue: PropTypes.string.isRequired,
  sentinelLabel: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  insideModal: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium"]),
  fullWidth: PropTypes.bool,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  inputSx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};
